import { useApolloClient } from '@apollo/client';
import { useEffect, useState, type PropsWithChildren } from 'react';

/** Unmount old observers and clear their cache before a different viewer can issue queries. */
export function ApolloViewerBoundary({ viewerId, children }: PropsWithChildren<{ viewerId?: string }>) {
  const client = useApolloClient();
  const [viewer, setViewer] = useState({ id: viewerId, hasAuthenticatedViewer: viewerId !== undefined });
  const [resetError, setResetError] = useState(false);

  // Before the first authenticated viewer there is no previous user's private cache to isolate.
  // Adopt that identity without remounting public pages or interrupting their auth/bootstrap work.
  if (!viewer.hasAuthenticatedViewer && viewerId !== undefined) {
    setViewer({ id: viewerId, hasAuthenticatedViewer: true });
  }

  useEffect(() => {
    if (!viewer.hasAuthenticatedViewer || viewer.id === viewerId) return;
    let active = true;
    void client.clearStore().then(
      () => {
        if (active) {
          setResetError(false);
          setViewer({ id: viewerId, hasAuthenticatedViewer: true });
        }
      },
      () => {
        if (active) setResetError(true);
      },
    );
    return () => {
      active = false;
    };
  }, [client, viewer, viewerId]);

  if (viewer.hasAuthenticatedViewer && viewer.id !== viewerId) {
    return resetError ? <p role="alert">Your session changed. Refresh the page to continue.</p> : null;
  }
  return children;
}
